import { FC, Fragment, useEffect, useState } from "react";
import { DialogWrapper } from "components/shared/dialog";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid, Tooltip } from "@material-ui/core";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup";
import { Input } from "components/shared/input";
import { postCustomCTA } from "adapters";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomCTA,
  GetUrlBuilderData,
  SetUrlBuilderData,
} from "store/url-builder";
import { useParams } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";

import style from "./custom-cta.module.scss";
import { DeleteForever } from "@material-ui/icons";
import { ColorPicker } from "../../../shared/color-picker";
import ColorPickerDialogue from "components/shared/color-picker-dialogue";

const schema = yup.object().shape({
  ctaList: yup.array().of(
    yup.object().shape({
      buttonText: yup.string().trim().required().label("Button Text"),
      buttonLink: yup.string().trim().required().url().label("URL"),
      buttonColor: yup.string().trim().required().label("Button Color"),
    })
  ),
});
export interface CustomCTADialogProps {
  isOpen: boolean;
  onClose(): void;
  builderType: string;
  ctaList: CustomCTA[];
}

export const CustomCTADialog: FC<CustomCTADialogProps> = ({
  isOpen,
  onClose,
  builderType,
  ctaList,
}) => {
  const dispatch = useDispatch();
  const builderData = useSelector(GetUrlBuilderData);
  const [loading, setLoading] = useState(false);
  const { cardId }: { cardId: string } = useParams();

  const [colorPicker, setColorPicker] = useState(false);

  const [colorPickerOptions, setColorPickerOptions] = useState({
    selectedColor: "",
    onColorSelect: () => {},
  });

  const submit = (data: any) => {
    if (Object.keys(errors).length) return;

    const _ctaList: CustomCTA = {
      buttonText: "",
      buttonLink: "",
      buttonColor: "",
    };

    if (data?.ctaList) {
      (data.ctaList as CustomCTA[]).forEach((cta) => {
        _ctaList.buttonText = _ctaList.buttonText
          ? _ctaList.buttonText + cta.buttonText + "#lcard;"
          : cta.buttonText + "#lcard;";
        _ctaList.buttonLink = _ctaList.buttonLink
          ? _ctaList.buttonLink + cta.buttonLink + "#lcard;"
          : cta.buttonLink + "#lcard;";
        _ctaList.buttonColor = _ctaList.buttonColor
          ? _ctaList.buttonColor + cta.buttonColor + "#lcard;"
          : cta.buttonColor + "#lcard;";
      });
    }

    setLoading(true);
    postCustomCTA(cardId, builderType, _ctaList)
      .then(({ data }) => {
        if (!data.success) return;
        dispatch(SetUrlBuilderData(data.data));
        onClose();
      })
      .finally(() => setLoading(false));
  };

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ctaList",
  });

  const addNewCtaButton = () => {
    append({
      buttonText: "",
      buttonLink: "",
      buttonColor: builderData.primaryColor.color,
    });
  };
  const removeCtaButton = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    append(ctaList);
  }, [ctaList]);

  return (
    <Fragment>
      <DialogWrapper
        open={isOpen}
        onClose={(event, reason) => {
          if (reason && reason === "backdropClick") return false;
          onClose();
        }}
        title="Custom CTA Button"
        loading={loading}
        onSubmit={handleSubmit(submit)}
      >
        {fields.map((item, index) => (
          <Grid container item xs={12} className="mb-3" key={item.id}>
            <Grid xs className="mr-2">
              <Controller
                control={control}
                name={`ctaList[${index}].buttonText`}
                key={item.id}
                render={({ field: { value, onChange } }) => (
                  <Input
                    label="Button Text"
                    error={
                      errors.ctaList && !!errors?.ctaList[index]?.buttonText
                    }
                    helperText={
                      (errors?.ctaList &&
                        errors.ctaList[index]?.buttonText?.message) ||
                      ""
                    }
                    fullWidth
                    disabled={loading}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Grid>
            <Grid xs>
              <Controller
                control={control}
                name={`ctaList[${index}].buttonLink`}
                key={item.id}
                render={({ field: { value, onChange } }) => (
                  <Input
                    label="Enter Url"
                    error={
                      errors.ctaList && !!errors?.ctaList[index]?.buttonLink
                    }
                    helperText={
                      (errors?.ctaList &&
                        errors.ctaList[index]?.buttonLink?.message) ||
                      ""
                    }
                    fullWidth
                    disabled={loading}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Grid>

            <Grid
              item
              container
              alignItems="center"
              xs={2}
              sm={1}
              className={`${style.options} ${
                errors.ctaList &&
                (!!errors?.ctaList[index]?.buttonLink ||
                  !!errors?.ctaList[index]?.buttonText)
                  ? style.onError
                  : ""
              }`}
            >
              <Controller
                control={control}
                name={`ctaList[${index}].buttonColor`}
                key={item.id}
                render={({ field: { value, onChange } }) => {
                  return (
                    <Tooltip title="Pick Button Color">
                      <Box
                        className={style.colourItem}
                        bgcolor={value}
                        onClick={() => {
                          setColorPickerOptions({
                            selectedColor: value,
                            onColorSelect: onChange,
                          });
                          setColorPicker(true);
                        }}
                      />
                    </Tooltip>
                  );
                }}
              />

              <Box
                onClick={() => removeCtaButton(index)}
                className={`${style.remove} ${style.optionItem}`}
              >
                <DeleteForever color="error" />
              </Box>
            </Grid>
          </Grid>
        ))}
        <Grid item container xs={12} justifyContent="center">
          <Button onClick={addNewCtaButton} style={{ textTransform: "none" }}>
            <AddIcon /> Add New CTA Button
          </Button>
        </Grid>
      </DialogWrapper>
      {colorPicker && (
        <ColorPickerDialogue
          isOpen={colorPicker}
          onClose={() => {
            setColorPicker(false);
          }}
          selectedColor={colorPickerOptions.selectedColor}
          onColorSelect={colorPickerOptions.onColorSelect}
        />
      )}
    </Fragment>
  );
};
